import { useState, useEffect } from 'react';
import {MusicStructureAnnotationFormAttributes} from "../lib/definitions/MusicStructureAnnotation";
import {FixedDoChordAttributes, MovableDoChordAttributes} from "../lib/definitions/ChordTrackAnnotation";
import {
  InstrumentAnnotationFormAttributes,
  StringInstrumentAnnotationFormAttributes,
  isStringInstrument,
} from "../lib/definitions/InstrumentTrackAnnotation";
import annotationDataService from '../services/AnnotationDataService';
import { arrayMove } from '@dnd-kit/sortable';
import { COLORS } from '../utils/constants';


export const useTracks = () => {
  // State for tracks
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [ chordLabellingSystem, setChordLabellingSystem ] = useState("");
  
  // State for instrument change confirmation dialog
  const [instrumentChangeDialog, setInstrumentChangeDialog] = useState({
    open: false,
    changeType: null,
    oldInstrumentName: '',
    newInstrumentName: '',
    trackId: '',
    newTrackId: '',
    pendingCallback: null
  });
  
  // Effect to initialize tracks based on the loaded sample.json data
  useEffect(() => {
    // Function to load tracks from annotation sheet data
    const loadTracksFromAnnotationSheet = () => {
      // If annotation sheet is not loaded yet, initialize with default tracks
      if (!annotationDataService.annotationSheet) {
        // console.log('Annotation sheet not loaded yet, using default tracks');
        setTracks([
          {
            id: "music-structure-track",
            name: "Music Structure Track",
            type: "structure",
            collapsed: false,
            unlocked: true,
            annotation_form_attributes: MusicStructureAnnotationFormAttributes,
            colorType: COLORS.TRACK_PIANO
          },
          {
            id: "chord-track",
            name: "Chord Track",
            type: "chord",
            collapsed: false,
            unlocked: false,
            annotation_form_attributes: null,
            colorType: COLORS.TRACK_CHORD
          }
        ]);
        return;
      }
      
      const annotationSheet = annotationDataService.annotationSheet;
      
      // Create tracks array to populate
      const dynamicTracks = [];
      
      // Always add Music Structure track if metadata_tracks exists
      if (annotationSheet.metadata_tracks && annotationSheet.metadata_tracks.music_structure_track) {
        dynamicTracks.push({
          id: "music-structure-track",
          name: "Music Structure Track",
          type: "structure",
          collapsed: false,
          unlocked: true,
          annotation_form_attributes: MusicStructureAnnotationFormAttributes,
          colorType: COLORS.TRACK_PIANO
        });
      }
      
      // Add Chord Track if chord_track exists in metadata_tracks
      if (annotationSheet.metadata_tracks && annotationSheet.metadata_tracks.chord_track) {
        const labelingSystem = annotationSheet.labeling_system || "";
        setChordLabellingSystem(labelingSystem);
        const isFixed = labelingSystem.includes("Fixed-Do");

        dynamicTracks.push({
          id: "chord-track",
          name: "Chord Track",
          type: "chord",
          collapsed: false,
          unlocked: labelingSystem !== "", // Auto-unlock if labeling system is already selected
          annotation_form_attributes: isFixed ? FixedDoChordAttributes : MovableDoChordAttributes,
          colorType: COLORS.TRACK_CHORD
        });
      }
      
      // Add instrument tracks from the data
      if (annotationSheet.instrument_tracks && annotationSheet.instrument_tracks.length > 0) {
        annotationSheet.instrument_tracks.forEach((track, index) => {
          // Debug logging to understand track structure
          if (!track.name) {
            console.warn(`Track at index ${index} has no name property:`, track);
          }
          
          // Extract instrument name from the full instrument string
          // Example: "Keyboard (Keyboard) >> Organ (Organ) >> Pipe Organ (Pipe Organ)"
          // const instrumentParts = track.instrument.split('>>');
          
          // Get the most specific instrument (last part of the path)
          // const specificInstrument = instrumentParts[instrumentParts.length - 1].trim();
          // let baseInstrument = instrumentParts[0].trim().split('(')[0].trim();
          // populate older tracks
          if (!track.colorType) {
            track.colorType = getRandomColorType();
          }

          // Determine appropriate form attributes based on instrument type
          const instrumentPath = track.instrument || "";
          const trackName = track.name || "";
          // Check both instrument path and track name for string detection
          const isStringFromPath = isStringInstrument(instrumentPath);
          const isStringFromName = isStringInstrument(trackName);
          const isString = isStringFromPath || isStringFromName;
          console.log(`Track: ${trackName}, Instrument: ${instrumentPath}, IsStringFromPath: ${isStringFromPath}, IsStringFromName: ${isStringFromName}, IsString: ${isString}`);
          const formAttributes = isString 
            ? StringInstrumentAnnotationFormAttributes 
            : InstrumentAnnotationFormAttributes;

          dynamicTracks.push({
            id: track.track_id,
            name: track.name || track.track_id || 'Unknown Track', // Use the most specific instrument name instead of the full path
            fullInstrumentPath: instrumentPath, // Still store the full instrument path for reference
            type: "instrument",
            collapsed: false,
            unlocked: true,
            annotation_form_attributes: formAttributes,
            colorType: track.colorType,
            is_main_melody: Boolean(track.is_main_melody), // Load the main melody state from backend
            marked_for_deletion: Boolean(track.marked_for_deletion) // Load the marked for deletion state from backend
          });
        });
      }
      
      // console.log('Dynamically loaded tracks:', dynamicTracks);
      // note we need to keep the previous collapsed state
      // setTracks(prevTracks => {
      //   const existingCollapsed = prevTracks.reduce((map, t) => {
      //     map[t.id] = t.collapsed;
      //     return map;
      //   }, {});
      //   return dynamicTracks.map(t => ({
      //     ...t,
      //     collapsed: existingCollapsed[t.id] ?? t.collapsed,
      //   }));
      // })
      // COMMENTED OUT: prevTrackrelated logic
      
      // preserve collapsed AND instrument-order from existing state
      // setTracks(prevTracks => {
      //   // 1) map of old collapsed flags
      //   const existingCollapsed = prevTracks.reduce((map, t) => {
      //     map[t.id] = t.collapsed;
      //     return map;
      //   }, {});

      //   // 2) split dynamicTracks into non-instrument vs instrument
      //   const nonInstr = dynamicTracks.filter(t => t.type !== 'instrument');
      //   const instr    = dynamicTracks.filter(t => t.type === 'instrument');

      //   // 3) grab previous instrument ordering
      //   const prevInstrOrder = prevTracks.map(t => t.id);

      //   // 4) sort new instrument tracks by index in prevInstrOrder,
      //   //    newcomers (indexOf === -1) go to the end in original order
      //   instr.sort((a, b) => {
      //     const ia = prevInstrOrder.indexOf(a.id);
      //     const ib = prevInstrOrder.indexOf(b.id);
      //     if (ia === -1 && ib === -1) return 0;
      //     if (ia === -1) return 1;
      //     if (ib === -1) return -1;
      //     return ia - ib;
      //   });

      //   // 5) recombine, applying collapsed state
      //   return [...nonInstr, ...instr].map(t => ({
      //     ...t,
      //     collapsed: existingCollapsed[t.id] ?? t.collapsed,
      //   }));
      // });

      // Simple direct assignment - use backend order only
      setTracks(dynamicTracks);
    };
    
    // Register listener for annotation sheet changes
    const unregister = annotationDataService.registerListener(() => {
      loadTracksFromAnnotationSheet();
    });
    
    // Initial load
    loadTracksFromAnnotationSheet();
    
    // Cleanup listener on unmount
    return () => {
      if (unregister) unregister();
    };
  }, []);

  // Function to get a random track color type
  const getRandomColorType = () => {
    const randomIndex = Math.floor(Math.random() * COLORS.BAR_LEVEL_COLORS.length);
    return COLORS.BAR_LEVEL_COLORS[randomIndex];
  };

  const deleteTrack = (trackId) => {
    if (trackId === 'chord-track' || trackId.includes('music-structure')) return;
    if (!annotationDataService.removeTrack(trackId)) return false;
    setTracks(prevTracks => prevTracks.filter(track => track.id !== trackId));
    setSelectedTrackId(selectedId => selectedId === trackId ? null : selectedId);
    return true;
  }

  // TODO: update how we deal with trackName if we take in full path instead
  const addNewTrack = (trackName, trackType) => {
    // Get appropriate form attributes based on track type and name
    const isString = isStringInstrument(trackName);
    console.log(`Adding new track: ${trackName}, IsString: ${isString}`);
    const formAttributes = isString 
      ? StringInstrumentAnnotationFormAttributes 
      : InstrumentAnnotationFormAttributes;
    
    const newTrackInstanceIdx = annotationDataService.generateNextTrackIndex(trackName);
    const trackIdPrefix = trackName.concat("-track");
    const requestedTrackId = trackIdPrefix.concat("_" + newTrackInstanceIdx.toString());
    const color = getRandomColorType()
    const track = {
      id: requestedTrackId,
      name: trackName,
      type: trackType,
      collapsed: false,
      unlocked: true,
      annotation_form_attributes: formAttributes,
      colorType: color, // Assign a random color type
      is_main_melody: false, // Default to false for new tracks
      marked_for_deletion: false // Default to false for new tracks
    }
    const createdTrackId = annotationDataService.createTrack({
      track_id: requestedTrackId,
      name: trackName,
      type: trackType,
      annotation_form_attributes: formAttributes,
      annotation_blocks: [],
      is_main_melody: false,
      marked_for_deletion: false,
      colorType: color
    });
    if (!createdTrackId) return false;
    setTracks(prevTracks => prevTracks.some(existing => existing.id === createdTrackId)
      ? prevTracks
      : [...prevTracks, { ...track, id: createdTrackId }]
    );
    return createdTrackId;
  };

  const editTrack = async (trackId, newTrackId, newTrackName) => {
    // Find the current track to check for instrument type changes
    const currentTrack = tracks.find(t => t.id === trackId);
    if (!currentTrack) {
      console.error('Track not found:', trackId);
      return;
    }

    const oldIsString = isStringInstrument(currentTrack.name);
    const newIsString = isStringInstrument(newTrackName);
    
    // Check if instrument type is changing
    if (oldIsString !== newIsString) {
      const changeType = oldIsString && !newIsString ? 'stringToNonString' : 'nonStringToString';
      
      // Show confirmation dialog
      setInstrumentChangeDialog({
        open: true,
        changeType,
        oldInstrumentName: currentTrack.name,
        newInstrumentName: newTrackName,
        trackId,
        newTrackId,
        pendingCallback: () => performTrackEdit(trackId, newTrackId, newTrackName)
      });
      return;
    }
    
    // No instrument type change, proceed directly
    await performTrackEdit(trackId, newTrackId, newTrackName);
  };

  const performTrackEdit = async (trackId, newTrackId, newTrackName) => {
    const authoritativeTrackId = await annotationDataService.renameTrack(
      trackId,
      newTrackId,
      newTrackName
    );
    if (!authoritativeTrackId) return false;
    
    // Update the UI state with new form attributes
    setTracks(prevTracks =>
        prevTracks.map(track => {
          if (track.id === trackId || track.id === authoritativeTrackId) {
            // Determine new form attributes based on new instrument name
            const newIsString = isStringInstrument(newTrackName);
            const newFormAttributes = newIsString 
              ? StringInstrumentAnnotationFormAttributes 
              : InstrumentAnnotationFormAttributes;
              
            return {
              ...track,
              id: authoritativeTrackId,
              name: newTrackName,
              annotation_form_attributes: newFormAttributes
            };
          }
          return track;
        })
    );
    setSelectedTrackId(selectedId => selectedId === trackId ? authoritativeTrackId : selectedId);
    return authoritativeTrackId;
  };

  const handleInstrumentChangeConfirm = () => {
    if (instrumentChangeDialog.pendingCallback) {
      instrumentChangeDialog.pendingCallback();
    }
    setInstrumentChangeDialog({
      open: false,
      changeType: null,
      oldInstrumentName: '',
      newInstrumentName: '',
      trackId: '',
      newTrackId: '',
      pendingCallback: null
    });
  };

  const handleInstrumentChangeCancel = () => {
    setInstrumentChangeDialog({
      open: false,
      changeType: null,
      oldInstrumentName: '',
      newInstrumentName: '',
      trackId: '',
      newTrackId: '',
      pendingCallback: null
    });
  };

  const reorderTracks = (activeId, overId) => {
    setTracks(prev => {
      const oldIndex = prev.findIndex(t => t.id === activeId);
      const newIndex = prev.findIndex(t => t.id === overId);
      return arrayMove(prev, oldIndex, newIndex);
    });
    // trackManagementService.reorderTracks(activeId, overId);
  };

  const toggleTrackCollapse = (trackId) => {
    setTracks(tracks.map(t =>
      t.id === trackId ? { ...t, collapsed: !t.collapsed } : t
    ));
  };

  const unlockChordTrack = (system) => {
    setChordLabellingSystem(system);
    if (annotationDataService.annotationSheet) {
      annotationDataService.annotationSheet.labeling_system = system;
      annotationDataService.notifyListeners();
    }
    setTracks(tracks.map(t =>
        t.id === 'chord-track' ? { ...t, unlocked: true, annotation_form_attributes: (system.includes("Fixed-Do") ? FixedDoChordAttributes : MovableDoChordAttributes) } : t
    ));
    if (system.includes("Movable-Do")) {
      window.alert("You have selected the Movable-Do Solfège System. Please label the chords according to the key you previously selected. For example, in the key of A minor, the Am chord should be labeled as chord degree i instead of vi.\n")
    }
  };

  const lockChordTrack = (trackId) => {
    setChordLabellingSystem("");
    if (annotationDataService.annotationSheet) {
      annotationDataService.annotationSheet.labeling_system = "";
    }
    setTracks(tracks.map(t =>
        t.id === trackId ? { ...t, unlocked: false } : t
    ));
    annotationDataService.notifyListeners();
  }

  const handleTrackSelect = (trackId) => {
    setSelectedTrackId(trackId);
  }

  const toggleMainMelodyTrack = (trackId) => {
    // Update the UI state
    setTracks(prevTracks =>
      prevTracks.map(track => {
        if (track.id === trackId) {
          return { ...track, is_main_melody: !track.is_main_melody };
        }
        return track;
      })
    );

    // Update the backend data
    const annotationSheet = annotationDataService.getAnnotationSheet();
    if (annotationSheet?.instrument_tracks) {
      const targetTrack = annotationSheet.instrument_tracks.find(t => t.track_id === trackId);
      if (targetTrack) {
        targetTrack.is_main_melody = !targetTrack.is_main_melody;
        annotationDataService.notifyListeners();
      }
    }
  };

  const toggleTrackForDeletion = (trackId) => {
    const currentTrack = tracks.find(t => t.id === trackId);
    if (!currentTrack) return false;
    const markedForDeletion = !currentTrack.marked_for_deletion;
    const authoritativeTrackId = annotationDataService.markTrackForDeletion(
      trackId,
      markedForDeletion
    );
    if (!authoritativeTrackId) return false;

    setTracks(prevTracks => prevTracks.map(track => track.id === trackId ? {
      ...track,
      id: authoritativeTrackId,
      name: markedForDeletion ? 'Delete' : 'Unknown',
      is_main_melody: false,
      marked_for_deletion: markedForDeletion
    } : track));
    setSelectedTrackId(selectedId => selectedId === trackId ? authoritativeTrackId : selectedId);
    return authoritativeTrackId;
  };

  return {
    tracks,
    selectedTrackId,
    handleTrackSelect,
    addNewTrack,
    deleteTrack,
    toggleTrackCollapse,
    chordLabellingSystem,
    unlockChordTrack,
    lockChordTrack,
    editTrack,
    reorderTracks,
    toggleMainMelodyTrack,
    toggleTrackForDeletion,
    instrumentChangeDialog,
    handleInstrumentChangeConfirm,
    handleInstrumentChangeCancel
  };
};
